import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

export const SITE_URL = "https://www.amgroupofeducation.com";
export const SITE_NAME = "AM Group of Education";
export const OG_IMAGE = `${SITE_URL}/favicon-512x512.png`;
export const ORG_LOGO = `${SITE_URL}/favicon-512x512.png`;

/**
 * Central SEO component. Renders per-page:
 *  - <title>
 *  - meta description
 *  - canonical (built from SITE_URL + current pathname)
 *  - Open Graph tags (og:title, og:description, og:url, og:image, og:type, og:site_name, og:locale)
 *  - Twitter card tags
 *  - optional JSON-LD blocks passed via `jsonLd` prop (array of objects)
 *  - optional noindex flag
 */
export default function SEO({
  title,
  description,
  path,
  image = OG_IMAGE,
  type = "website",
  jsonLd = [],
  noindex = false,
}) {
  const location = useLocation();
  const canonical = SITE_URL + (path ?? location.pathname);
  const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;

  return (
    <Helmet prioritizeSeoTags>
      <html lang="en-IN" />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content="en_IN" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {jsonLd.map((obj, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(obj)}</script>
      ))}
    </Helmet>
  );
}

/* ----------- JSON-LD builders ----------- */

export const ORG_JSONLD = {
  "@context": "https://schema.org",
  "@type": "EducationalOrganization",
  name: SITE_NAME,
  alternateName: "AM Group",
  url: SITE_URL,
  logo: {
    "@type": "ImageObject",
    url: ORG_LOGO,
    width: 512,
    height: 512,
  },
  image: ORG_LOGO,
  slogan: "Learn Today, Lead Tomorrow",
  description:
    "Premium coaching institute offering School Education (Classes 1–12, SSC/CBSE/ICSE), Competitive Exams (NEET, JEE, Foundation), Government Exams (UPSC, MPSC, SSC, Banking, Railway, Defence, Teaching), Language Courses and IT Courses under one roof.",
  founder: {
    "@type": "Person",
    name: "Arif Mulani",
    jobTitle: "Founder & Chief Executive Officer",
  },
  contactPoint: {
    "@type": "ContactPoint",
    telephone: "+91-8390860276",
    email: "amgroupofeducation@gmail.com",
    contactType: "admissions",
    availableLanguage: ["English", "Hindi", "Marathi"],
  },
  sameAs: [
    "https://www.instagram.com/am_group_of_education",
    "https://wa.me/918390860276",
  ],
};

export function breadcrumb(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: SITE_URL + it.path,
    })),
  };
}

export function course(name, description, url) {
  return {
    "@context": "https://schema.org",
    "@type": "Course",
    name,
    description,
    provider: {
      "@type": "EducationalOrganization",
      name: SITE_NAME,
      sameAs: SITE_URL,
    },
    url: SITE_URL + url,
  };
}
